/**
 * @author michalstolarski
 * @since 2022-3-29
 */

import * as React from "react";
import styled, { css } from "styled-components";

import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { BottomSheet } from "@ui/Atoms/BottomSheet";
import { Backdrop } from "@ui/Molecules/Backdrop";

enum ANIMATION_STATE {
  IDLE = "IDLE",
  OPENING = "OPENING",
  OPENED = "OPENED",
  CLOSING = "CLOSING",
  CLOSED = "CLOSED",
}

interface Props {
  testId?: string;
  className?: string;
  children: React.ReactNode;
  onClose: () => void;

  /** Controlling closing by a parent component */
  forcedClosing?: boolean;
  setForcedClosing?: (value: boolean) => void;
}

const BottomSheetModal = (props: Props): React.ReactElement => {
  const { forcedClosing } = props;
  const [state, setState] = React.useState<ANIMATION_STATE>(
    ANIMATION_STATE.IDLE,
  );

  const closingModal = () => {
    setState(ANIMATION_STATE.CLOSING);
  };

  const onTransitionEnd = () => {
    switch (state) {
      case ANIMATION_STATE.OPENING:
        setState(ANIMATION_STATE.OPENED);
        break;
      case ANIMATION_STATE.CLOSING:
        setState(ANIMATION_STATE.CLOSED);
        break;
    }
  };

  const setToggleState = (sheetState: boolean): void => {
    if (sheetState) return;

    closingModal();
  };

  React.useEffect(() => {
    if (!forcedClosing) return;

    setState(ANIMATION_STATE.CLOSING);
  }, [forcedClosing]);

  React.useEffect(() => {
    switch (state) {
      case ANIMATION_STATE.IDLE:
        props.setForcedClosing?.(false);

        setState(ANIMATION_STATE.OPENING);
        return;
      case ANIMATION_STATE.CLOSED:
        props.onClose();
        return;
    }
  }, [state]);

  return (
    <Backdrop onClick={closingModal}>
      <TestWrapper testId={props.testId}>
        <BottomSheetStyled
          animationState={state}
          setToggleState={setToggleState}
          onTransitionEnd={onTransitionEnd}
          hideCurtain
          initState
        >
          {props.children}
        </BottomSheetStyled>
      </TestWrapper>
    </Backdrop>
  );
};

export { BottomSheetModal };

const BottomSheetStyled = styled(BottomSheet)<{
  animationState: ANIMATION_STATE;
}>`
  max-height: 100vh;
  padding: ${({ theme }) =>
    `${theme.margins.base_x1_5} 0 ${theme.margins.base_x2}`};
  font-size: ${props => props.theme.fonts.sizes.body};
  line-height: ${props => props.theme.fonts.lineHeights.normal};
  transform: translateY(110%);
  transition: transform
    ${({ theme }) => `${theme.animations.duration} ${theme.animations.easing}`};

  ${props =>
    [ANIMATION_STATE.OPENING, ANIMATION_STATE.OPENED].includes(
      props.animationState,
    ) &&
    css`
      transform: translateY(0);
    `}
`;
